import React, { useState, useEffect, useRef } from 'react';

import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getSPDChapterCommittees,
  addSPDChapterCommitteeMember,
  updateSPDChapterCommitteeMember,
  replaceSPDChapterCommitteeMember,
  deleteSPDChapterCommitteeMember,
  updateSPDChapterCommittee,
  createSPDChapterCommittee,
  getSPDChapterCommitteePositions,
  getSPDChapterCommitteeTypes,
} from '../../../endpoints/chaptersTs';
import { roles } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';
import {
  SPDChapterCommittee,
  SPDChapterCommitteeMemberPost,
  SPDChapterCommitteeMemberPut,
  SPDChapterCommitteeMemberReplace,
  SPDChapterCommitteePut,
  SPDChapterCommitteePost,
  SPDChapterCommitteeType,
} from '../../../types/committee';

interface SPDCommitteesProps {
  id: string;
  spdAccess: boolean;
}

interface SaveData {
  action: 'newAppointment' | 'editDates' | 'removeRecord';
  userId?: string;
  startDate?: string | null;
  endDate?: string | null;
  positionId?: string;
  committeeId?: string;
  id?: string;
}

const SPDCommittees: React.FC<SPDCommitteesProps> = ({ id, spdAccess }) => {
  // Helper function to format date to YYYY-MM-DD format
  const formatDateForAPI = (
    dateString: string | null | undefined
  ): string | null => {
    if (!dateString) return null;
    const date = dayjs(dateString);
    return date.isValid() ? date.format('YYYY-MM-DD') : null;
  };

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [filteredCommittees, setFilteredCommittees] = useState<
    SPDChapterCommittee[]
  >([]);
  const [editingCommittee, setEditingCommittee] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<SPDChapterCommitteePut>>({});
  const [isDataRefreshing, setIsDataRefreshing] = useState<boolean>(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [createData, setCreateData] = useState<
    Partial<SPDChapterCommitteePost>
  >({
    name: '',
    description: '',
    group_type_id: '',
    is_active: true,
  });
  const scrollToCommitteeRef = useRef<string | null>(null);
  const savedScrollPositionRef = useRef<number>(0);

  const {
    data: committeesData,
    isFetching,
    refetch,
  } = useApiGet(['spd-committees', id], () => getSPDChapterCommittees(id), {
    enabled: true,
    retry: 1,
  });

  const { data: committeeTypes } = useApiGet<SPDChapterCommitteeType[]>(
    ['spd-committee-types'],
    () => getSPDChapterCommitteeTypes(),
    {
      enabled: true,
      retry: 1,
    }
  );

  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
  };

  useEffect(() => {
    if (committeesData) {
      setFilteredCommittees(
        committeesData.filter((committee) => {
          const matchesSearch = committee.name
            .toLowerCase()
            .includes(search.toLowerCase());
          const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' && committee.is_active) ||
            (statusFilter === 'inactive' && !committee.is_active);
          return matchesSearch && matchesStatus;
        })
      );
    }
  }, [committeesData, search, statusFilter]);

  // State to hold positions for each committee (loaded on-demand)
  const [committeePositions, setCommitteePositions] = useState<{
    [committeeId: string]: any[];
  }>({});
  const [loadingPositions, setLoadingPositions] = useState<{
    [committeeId: string]: boolean;
  }>({});

  // Function to load positions for a specific committee
  const loadCommitteePositions = async (committeeId: string) => {
    // Don't reload if already loaded
    if (committeePositions[committeeId]) {
      return;
    }

    setLoadingPositions((prev) => ({ ...prev, [committeeId]: true }));
    try {
      const positions = await getSPDChapterCommitteePositions(id, committeeId);
      setCommitteePositions((prev) => ({ ...prev, [committeeId]: positions }));
    } catch (error) {
      console.error('Error loading committee positions:', error);
      setErrorSaving('Failed to load positions for this committee');
    } finally {
      setLoadingPositions((prev) => ({ ...prev, [committeeId]: false }));
    }
  };

  const { mutate: updateCommittee } = useApiSend(
    (data: { committeeId: string } & SPDChapterCommitteeMemberPut) =>
      updateSPDChapterCommitteeMember(id, data.committeeId, {
        start_date: formatDateForAPI(data.start_date) || '',
        end_date: formatDateForAPI(data.end_date),
      }),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error updating committee member, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteCommittee } = useApiSend(
    (data: { committeeId: string }) =>
      deleteSPDChapterCommitteeMember(id, data.committeeId),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error deleting committee member, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: postCommittee } = useApiSend(
    (data: { committeeId: string } & SPDChapterCommitteeMemberPost) =>
      addSPDChapterCommitteeMember(id, data.committeeId, {
        user_id: data.user_id,
        start_date: formatDateForAPI(data.start_date) || '',
        end_date: formatDateForAPI(data.end_date),
        role_id: data.role_id,
      }),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error adding committee member, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceCommittee } = useApiSend(
    (data: { committeeId: string } & SPDChapterCommitteeMemberReplace) =>
      replaceSPDChapterCommitteeMember(id, data.committeeId, {
        user_id: data.user_id,
        start_date: formatDateForAPI(data.start_date) || '',
        end_date: formatDateForAPI(data.end_date),
      }),
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error replacing committee member, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateCommitteeDetails } = useApiSend(
    (data: { committeeId: string } & SPDChapterCommitteePut) =>
      updateSPDChapterCommittee(id, data.committeeId, {
        name: data.name,
        description: data.description,
        group_type_id: data.group_type_id,
        is_active: data.is_active,
      }),
    (data) => {
      setIsSaving(false);
      setEditingCommittee(null);
      setEditData({});
      setIsDataRefreshing(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error updating committee, if problem persists, please contact support'
      );
      setIsSaving(false);
      setIsDataRefreshing(false);
    }
  );

  const { mutate: createCommittee } = useApiSend(
    (data: SPDChapterCommitteePost) => createSPDChapterCommittee(id, data),
    (data) => {
      setIsSaving(false);
      setShowCreateDialog(false);
      setCreateData({
        name: '',
        description: '',
        is_active: true,
      });
      setIsDataRefreshing(false);
      refetch();
    },
    (error: Error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error creating committee, if problem persists, please contact support'
      );
      setIsSaving(false);
      setIsDataRefreshing(false);
    }
  );

  const onSavePosition = (position: SaveData) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        postCommittee({
          committeeId: position.committeeId!,
          user_id: position.userId!,
          start_date: position.startDate!,
          end_date: position.endDate || '',
          role_id: position.positionId!, // positionId maps to role_id for backend
        });
      } else {
        replaceCommittee({
          committeeId: position.id,
          user_id: position.userId!,
          start_date: position.startDate!,
          end_date: position.endDate || '',
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateCommittee({
        committeeId: position.id!,
        start_date: position.startDate!,
        end_date: position.endDate || '',
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      deleteCommittee({
        committeeId: position.id!,
      });
    }
  };

  const canEdit = (): boolean => {
    if (spdAccess) {
      let userRole = localStorage.getItem('role');
      let rolesAllowed = [roles.Admin, roles.Support, roles.SPDOfficer];
      return rolesAllowed.includes(userRole);
    }
    return false;
  };

  const handleEditCommittee = (committee: SPDChapterCommittee) => {
    setEditingCommittee(committee.id);
    setEditData({
      name: committee.name,
      description: committee.description,
      group_type_id: committee.group_type_id,
      is_active: committee.is_active,
    });
    // Save to refs for persistence
    scrollToCommitteeRef.current = committee.id;
    savedScrollPositionRef.current = window.scrollY;
  };

  const handleSaveCommittee = () => {
    if (
      editingCommittee &&
      editData.name &&
      editData.description &&
      editData.group_type_id
    ) {
      setIsSaving(true);
      setErrorSaving(null);
      setIsDataRefreshing(true);
      updateCommitteeDetails({
        committeeId: editingCommittee,
        name: editData.name,
        description: editData.description,
        group_type_id: editData.group_type_id,
        is_active: editData.is_active,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingCommittee(null);
    setEditData({});
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
  };

  const handleCreateCommittee = () => {
    setIsSaving(true);
    setErrorSaving(null);
    createCommittee(createData as SPDChapterCommitteePost);
  };

  const handleCancelCreate = () => {
    setShowCreateDialog(false);
    setCreateData({
      name: '',
      description: '',
      group_type_id: '',
      is_active: true,
    });
  };

  // Scroll to committee after data refresh
  useEffect(() => {
    const committeeToScrollTo = scrollToCommitteeRef.current;
    const positionToRestore = savedScrollPositionRef.current;

    if (committeeToScrollTo && committeesData && !isDataRefreshing) {
      // Use a longer timeout to ensure everything is rendered
      setTimeout(() => {
        // First restore the saved scroll position
        window.scrollTo({
          top: positionToRestore,
          behavior: 'auto' as ScrollBehavior,
        });

        // Then scroll to the specific committee with a slight delay
        setTimeout(() => {
          const element = document.getElementById(
            `committee-${committeeToScrollTo}`
          );
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200);

        // Clean up refs
        scrollToCommitteeRef.current = null;
        savedScrollPositionRef.current = 0;
      }, 500);
    }
  }, [committeesData, isDataRefreshing]);

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} sx={{ pt: 2 }}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!committeesData) {
    return (
      <Grid item xs={12} textAlign={'center'} sx={{ pt: 2 }}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Search"
            id="search-field"
            size="small"
            value={search}
            fullWidth={true}
            onChange={(event) => handleSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={3}>
          {canEdit() && (
            <Button
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateDialog(true)}
              disabled={isSaving}
              fullWidth
              size="small"
            >
              Create Committee
            </Button>
          )}
        </Grid>
      </Grid>
      {filteredCommittees.map((committee) => (
        <Grid item xs={12} md={6} sx={{ p: 1 }} key={committee.id}>
          <Card
            id={`committee-${committee.id}`}
            sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}
          >
            <CardHeader
              title={
                editingCommittee === committee.id ? (
                  <TextField
                    value={editData.name}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                    size="small"
                    fullWidth
                    placeholder="Committee Name"
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6">{committee.name}</Typography>
                    <Chip
                      label={committee.is_active ? 'Active' : 'Inactive'}
                      color={committee.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                )
              }
              subheader={
                editingCommittee === committee.id ? (
                  <TextField
                    value={editData.description}
                    onChange={(e) =>
                      setEditData({ ...editData, description: e.target.value })
                    }
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    placeholder="Committee Description"
                  />
                ) : (
                  committee.description
                )
              }
              action={
                canEdit() &&
                editingCommittee !== committee.id && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditCommittee(committee)}
                    disabled={isSaving}
                    color="info"
                  >
                    Edit
                  </Button>
                )
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              {editingCommittee === committee.id && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small" required>
                      <InputLabel>Committee Type</InputLabel>
                      <Select
                        value={editData.group_type_id || ''}
                        label="Committee Type"
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            group_type_id: e.target.value,
                          })
                        }
                      >
                        {committeeTypes?.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={editData.is_active ? 'active' : 'inactive'}
                        label="Status"
                        onChange={(e) =>
                          setEditData({
                            ...editData,
                            is_active: e.target.value === 'active',
                          })
                        }
                      >
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sx={{ mt: 1 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 1,
                        justifyContent: 'flex-end',
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={handleSaveCommittee}
                        disabled={
                          isSaving ||
                          !editData.name ||
                          !editData.description ||
                          !editData.group_type_id
                        }
                        color="success"
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        startIcon={<CancelIcon />}
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        color="secondary"
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
              {!editingCommittee && (
                <Grid container spacing={2}>
                  {committee.members
                    ?.sort((a, b) => a.order - b.order)
                    .map((user) => (
                      <PositionDisplay
                        id={user.id}
                        position={user.position}
                        positionId={user.position_id}
                        committeeId={committee.id}
                        name={user.name}
                        startDate={user.start_date}
                        endDate={user.end_date}
                        canEdit={canEdit()}
                        isSaving={isSaving}
                        errorSavingText={errorSaving}
                        onSave={onSavePosition}
                        userId={user.user_id}
                        pageFrom={'spd-committees'}
                        key={user.id}
                        spdChapterId={id}
                      />
                    ))}
                  {canEdit() && (
                    <Grid item xs={12} sx={{ p: 1 }}>
                      <AddPosition
                        buttonText="Add Position"
                        committeeId={committee.id}
                        positions={committeePositions[committee.id] || []}
                        onAdd={onSavePosition}
                        isSaving={isSaving}
                        savingError={errorSaving}
                        saveSuccess={null}
                        spdChapterId={id}
                        localChapterId={null}
                        onLoadPositions={loadCommitteePositions}
                        isLoadingPositions={
                          loadingPositions[committee.id] || false
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Create Committee Dialog */}
      <Dialog
        open={showCreateDialog}
        onClose={handleCancelCreate}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Committee</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Committee Name"
                value={createData.name}
                onChange={(e) =>
                  setCreateData({ ...createData, name: e.target.value })
                }
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={createData.description}
                onChange={(e) =>
                  setCreateData({ ...createData, description: e.target.value })
                }
                fullWidth
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Committee Type</InputLabel>
                <Select
                  value={createData.group_type_id || ''}
                  label="Committee Type"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      group_type_id: e.target.value,
                    })
                  }
                >
                  {committeeTypes?.map((type) => (
                    <MenuItem key={type.id} value={type.id}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={createData.is_active ? 'active' : 'inactive'}
                  label="Status"
                  onChange={(e) =>
                    setCreateData({
                      ...createData,
                      is_active: e.target.value === 'active',
                    })
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCreate} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateCommittee}
            variant="contained"
            color="success"
            disabled={
              isSaving ||
              !createData.name ||
              !createData.description ||
              !createData.group_type_id
            }
          >
            Create Committee
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default SPDCommittees;
